"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AiChartLegendContent,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ComparisonChartTooltipContent,
  resolveAiLegendLayout,
} from "./ai-chart";
import {
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  EMPTY_VIZ_SERIES,
  ensurePngExportFormats,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipDiffValue,
  formatVizTooltipMetricValue,
  isRecord,
  isVizAxisReversed,
  normalizeVizExportFormats,
  resolveEffectiveXAxisTickDensity,
  resolveReferenceLineOverflowMode,
  resolveTimeSeriesReferenceLineAxisId,
  resolveVizActiveDotProps,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizPaletteMode,
  resolveVizReferenceAreas,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  resolveVizTooltipMode,
  resolveXAxisTickDensityConfig,
  resolveXAxisTickLayout,
  type VizAxisDirections,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizTooltipMode,
  type VizXAxisTickDensity,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  formatVizAxisDateLabel,
  formatVizTooltipDateLabel,
  formatVizTooltipDomainLabel,
  isIsoDateLike,
} from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveDisplayLabel } from "./label-resolver";
import {
  buildTimeSeriesFinitePointCountMap,
  hasRenderableTimeSeriesValue,
  normalizeRowsForAxis,
  resolveTimeSeriesAxisOuterMargin,
  resolveTimeSeriesAxisState,
  resolveTimeSeriesNumericAxisWidth,
  resolveTimeSeriesStaticDotProps,
} from "./time-series-core";
import {
  COMPARISON_SERIES_DASHARRAY,
  hasComparingDateRows,
  isComparisonSeriesKey,
  resolveTimeSeriesLineInterpolation,
  toComparingDateLabel,
  toComparisonSeriesKey,
  toCurrentSeriesKey,
} from "./time-series-line-utils";
import {
  resolveTimeSeriesChartHeight,
  TimeSeriesActionBar,
  useTimeSeriesVisibleSeries,
} from "./time-series-shared";
import { type VizExportFormat } from "./viz-actions";
import { type TimeSeriesYAxisMode } from "./y-axis-mode";

export type TimeSeriesLineProps = BaseComponentProps<{
  dataPath: string;
  xKey: string;
  series?: Array<{
    key: string;
    label?: string | null;
    color?: string | null;
    metricKey?: string | null;
    groupKey?: string | null;
    groupValue?: string | null;
    strokeDasharray?: string | null;
  }>;
  xLabel?: string | null;
  seriesLabels?: Record<string, string> | null;
  periodLabels?: {
    current?: string | null;
    comparison?: string | null;
  } | null;
  legendMode?: "pair" | "series" | null;
  axisTickStyle?: {
    comparisonMuted?: boolean | null;
    comparisonFontSizeDelta?: number | null;
  } | null;
  xAxisTicks?: { density?: VizXAxisTickDensity | null } | null;
  referenceLines?: Array<{
    value: number;
    label?: string | null;
    color?: string | null;
    axis?: "primary" | "secondary" | null;
  }> | null;
  referenceAreas?: Array<{
    from: number;
    to: number;
    label?: string | null;
    color?: string | null;
    opacity?: number | null;
    axis?: "primary" | "secondary" | null;
  }> | null;
  chartHeight?: number | null;
  yAxisMode?: TimeSeriesYAxisMode | null;
  axisDirections?: VizAxisDirections | null;
  allowSeriesToggle?: boolean | null;
  seriesToggleMode?: "single-metric" | "multi-metric" | null;
  defaultVisibleSeriesKeys?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
}>;

function tryGetIsoDateKey(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!isIsoDateLike(trimmed)) return null;
  return trimmed.slice(0, 10);
}

type CompareXAxisTickProps = {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown; index?: number };
};

function resolveSeriesToggleKey(entry: {
  key: string;
  metricKey?: string | null;
  groupValue?: string | null;
}): string {
  return entry.groupValue ?? entry.metricKey ?? entry.key;
}

function resolveSeriesToggleLabel(entry: {
  key: string;
  label?: string | null;
  groupValue?: string | null;
}): string {
  const label = entry.groupValue ?? entry.label ?? entry.key;
  return typeof label === "string" && label.trim().length > 0
    ? label
    : entry.key;
}

export function TimeSeriesLine({ props }: TimeSeriesLineProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    xKey,
    series,
    seriesLabels,
    periodLabels,
    legendMode,
    axisTickStyle,
    xAxisTicks,
    referenceLines,
    referenceAreas,
    chartHeight,
    yAxisMode,
    axisDirections,
    allowSeriesToggle,
    seriesToggleMode,
    defaultVisibleSeriesKeys,
    allowExport,
    exportFormats,
    axisPreset,
    tooltipMode,
    paletteMode,
  } = props;
  const resolvedTooltipMode = resolveVizTooltipMode(tooltipMode);
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const resolvedLegendMode = legendMode === "series" ? "series" : "pair";
  const resolvedPeriodLabels = {
    current:
      typeof periodLabels?.current === "string" &&
      periodLabels.current.trim().length > 0
        ? periodLabels.current.trim()
        : "Current period",
    comparison:
      typeof periodLabels?.comparison === "string" &&
      periodLabels.comparison.trim().length > 0
        ? periodLabels.comparison.trim()
        : "Previous period",
  };
  const comparisonFontSizeDelta =
    typeof axisTickStyle?.comparisonFontSizeDelta === "number"
      ? Math.max(
          -4,
          Math.min(0, Math.round(axisTickStyle.comparisonFontSizeDelta)),
        )
      : -1;
  const comparisonDateMuted = axisTickStyle?.comparisonMuted !== false;
  const resolvedXKey = xKey && xKey.length > 0 ? xKey : "date";
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLegendExpanded, setIsLegendExpanded] = useState(false);

  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);
  const seriesList = Array.isArray(series) ? series : EMPTY_VIZ_SERIES;

  const allToggleKeys = useMemo(() => {
    const toggleKeys: string[] = [];
    const seen = new Set<string>();
    for (const entry of seriesList) {
      const toggleKey = resolveSeriesToggleKey(entry);
      if (seen.has(toggleKey)) continue;
      seen.add(toggleKey);
      toggleKeys.push(toggleKey);
    }
    return toggleKeys;
  }, [seriesList]);
  const { visibleSeriesKeySet: visibleToggleKeySet, toggleSeriesVisibility } =
    useTimeSeriesVisibleSeries({
      allSeriesKeys: allToggleKeys,
      defaultVisibleSeriesKeys,
      seriesToggleMode,
    });

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );

  const sortedData = useMemo(() => {
    const rows = data;
    if (rows.length <= 1) return rows;

    const hasAnyIso = rows.some((row) =>
      Boolean(tryGetIsoDateKey(row[resolvedXKey])),
    );
    if (!hasAnyIso) return rows;

    return [...rows].sort((a, b) => {
      const aKey = tryGetIsoDateKey(a[resolvedXKey]);
      const bKey = tryGetIsoDateKey(b[resolvedXKey]);
      if (!aKey && !bKey) return 0;
      if (!aKey) return 1;
      if (!bKey) return -1;
      return aKey.localeCompare(bKey);
    });
  }, [data, resolvedXKey]);
  const hasComparingDate = useMemo(
    () => hasComparingDateRows(sortedData),
    [sortedData],
  );

  const availableDataKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of sortedData) {
      for (const key of Object.keys(row)) {
        if (key === resolvedXKey) continue;
        keys.add(key);
      }
    }
    return keys;
  }, [resolvedXKey, sortedData]);

  type RenderedSeriesEntry = {
    key: string;
    metricKey: string;
    toggleKey: string;
    groupKey: string | null;
    groupValue: string | null;
    label: string;
    color: string;
    isComparison: boolean;
    strokeDasharray: string | null;
  };

  const groupColorMap = useMemo(() => {
    const orderedGroupKeys = Array.from(
      new Set(
        seriesList
          .map((entry) => resolveSeriesToggleKey(entry))
          .filter((entry): entry is string => entry !== null),
      ),
    );

    return new Map(
      orderedGroupKeys.map((groupKey, index) => [
        groupKey,
        resolveVizSeriesColor({
          index,
          paletteMode: resolvedPaletteMode,
        }),
      ]),
    );
  }, [resolvedPaletteMode, seriesList]);

  const metricSeriesColorMap = useMemo(() => {
    return new Map(
      seriesList.map((entry, index) => {
        const toggleKey = resolveSeriesToggleKey(entry);
        const groupColor = groupColorMap.get(toggleKey) ?? null;
        const color = resolveVizSeriesColor({
          index,
          color: entry.color ?? groupColor,
          paletteMode: resolvedPaletteMode,
        });
        return [entry.key, color] as const;
      }),
    );
  }, [groupColorMap, resolvedPaletteMode, seriesList]);

  const allRenderedSeriesList = useMemo<RenderedSeriesEntry[]>(() => {
    const rendered: RenderedSeriesEntry[] = [];
    const addedSeriesKeys = new Set<string>();

    seriesList.forEach((entry) => {
      const toggleKey = resolveSeriesToggleKey(entry);
      const baseLabel = resolveDisplayLabel(
        seriesLabels?.[entry.key],
        entry.label,
        entry.key,
      );

      const normalizedCurrentKey = toCurrentSeriesKey(
        entry.key,
        availableDataKeys,
      );
      const currentKey = normalizedCurrentKey ?? entry.key;
      if (!availableDataKeys.has(currentKey)) return;

      const comparisonKey = hasComparingDate
        ? toComparisonSeriesKey(currentKey, availableDataKeys)
        : null;
      const hasExplicitPairLabels =
        Boolean(seriesLabels?.[currentKey]) &&
        Boolean(comparisonKey && seriesLabels?.[comparisonKey]);
      const currentLabelWhenPaired = hasExplicitPairLabels
        ? resolveDisplayLabel(
            seriesLabels?.[currentKey],
            entry.label,
            currentKey,
          )
        : `${baseLabel} (${resolvedPeriodLabels.current})`;

      const currentLabel =
        hasComparingDate && comparisonKey && resolvedLegendMode === "pair"
          ? currentLabelWhenPaired
          : baseLabel;
      if (!addedSeriesKeys.has(currentKey)) {
        rendered.push({
          key: currentKey,
          metricKey:
            typeof entry.metricKey === "string" && entry.metricKey.length > 0
              ? entry.metricKey
              : entry.key,
          toggleKey,
          groupKey:
            typeof entry.groupKey === "string" && entry.groupKey.length > 0
              ? entry.groupKey
              : null,
          groupValue:
            typeof entry.groupValue === "string" && entry.groupValue.length > 0
              ? entry.groupValue
              : null,
          label: currentLabel,
          color: metricSeriesColorMap.get(entry.key) ?? "var(--chart-1)",
          isComparison: false,
          strokeDasharray:
            typeof entry.strokeDasharray === "string" &&
            entry.strokeDasharray.length > 0
              ? entry.strokeDasharray
              : null,
        });
        addedSeriesKeys.add(currentKey);
      }

      if (comparisonKey && availableDataKeys.has(comparisonKey)) {
        const comparisonLabelWhenPaired = hasExplicitPairLabels
          ? resolveDisplayLabel(
              seriesLabels?.[comparisonKey],
              entry.label,
              comparisonKey,
            )
          : `${baseLabel} (${resolvedPeriodLabels.comparison})`;
        const comparisonLabel =
          resolvedLegendMode === "pair" ? comparisonLabelWhenPaired : baseLabel;
        if (!addedSeriesKeys.has(comparisonKey)) {
          rendered.push({
            key: comparisonKey,
            metricKey:
              typeof entry.metricKey === "string" && entry.metricKey.length > 0
                ? entry.metricKey
                : entry.key,
            toggleKey,
            groupKey:
              typeof entry.groupKey === "string" && entry.groupKey.length > 0
                ? entry.groupKey
                : null,
            groupValue:
              typeof entry.groupValue === "string" &&
              entry.groupValue.length > 0
                ? entry.groupValue
                : null,
            label: comparisonLabel,
            color: metricSeriesColorMap.get(entry.key) ?? "var(--chart-1)",
            isComparison: true,
            strokeDasharray:
              typeof entry.strokeDasharray === "string" &&
              entry.strokeDasharray.length > 0
                ? entry.strokeDasharray
                : null,
          });
          addedSeriesKeys.add(comparisonKey);
        }
      }
    });

    return rendered;
  }, [
    availableDataKeys,
    hasComparingDate,
    metricSeriesColorMap,
    resolvedLegendMode,
    resolvedPeriodLabels.comparison,
    resolvedPeriodLabels.current,
    seriesList,
    seriesLabels,
  ]);

  const finitePointCountBySeriesKey = useMemo(() => {
    return buildTimeSeriesFinitePointCountMap({
      rows: sortedData,
      seriesKeys: allRenderedSeriesList.map((entry) => entry.key),
    });
  }, [allRenderedSeriesList, sortedData]);

  const renderableSeriesList = useMemo(() => {
    return allRenderedSeriesList.filter((entry) =>
      hasRenderableTimeSeriesValue(finitePointCountBySeriesKey.get(entry.key)),
    );
  }, [allRenderedSeriesList, finitePointCountBySeriesKey]);

  const renderedSeriesList = useMemo(() => {
    return renderableSeriesList.filter((entry) =>
      visibleToggleKeySet.has(entry.toggleKey),
    );
  }, [renderableSeriesList, visibleToggleKeySet]);

  const renderableToggleKeySet = useMemo(() => {
    return new Set(renderableSeriesList.map((entry) => entry.toggleKey));
  }, [renderableSeriesList]);

  const currentToComparisonSeriesMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const seriesEntry of renderedSeriesList) {
      if (seriesEntry.isComparison) continue;
      const comparisonKey = toComparisonSeriesKey(
        seriesEntry.key,
        availableDataKeys,
      );
      if (!comparisonKey || !availableDataKeys.has(comparisonKey)) continue;
      map.set(seriesEntry.key, comparisonKey);
    }
    return map;
  }, [availableDataKeys, renderedSeriesList]);

  const percentKeys = useMemo(() => {
    return renderedSeriesList
      .map((entry) => entry.key)
      .filter((key) => isPercentFieldKey(key, metaByName));
  }, [metaByName, renderedSeriesList]);

  const percentBaseKeys = useMemo(() => {
    return renderedSeriesList
      .filter((entry) => !entry.isComparison)
      .map((entry) => entry.key)
      .filter((key) => isPercentFieldKey(key, metaByName));
  }, [metaByName, renderedSeriesList]);

  const metricGroupKeys = useMemo(() => {
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const entry of renderedSeriesList) {
      if (seen.has(entry.metricKey)) continue;
      seen.add(entry.metricKey);
      ordered.push(entry.metricKey);
    }
    return ordered;
  }, [renderedSeriesList]);

  const metricGroupPercentKeySet = useMemo(() => {
    const percentGroups = new Set<string>();
    for (const entry of renderedSeriesList) {
      if (!isPercentFieldKey(entry.key, metaByName)) continue;
      percentGroups.add(entry.metricKey);
    }
    return percentGroups;
  }, [metaByName, renderedSeriesList]);

  const axisState = useMemo(
    () =>
      resolveTimeSeriesAxisState({
        effectiveSeriesKeys: metricGroupKeys,
        percentKeySet: metricGroupPercentKeySet,
        yAxisMode,
      }),
    [metricGroupKeys, metricGroupPercentKeySet, yAxisMode],
  );
  const { useDualAxis, useNormalizedAxis } = axisState;
  const referenceLineOverflowMode = useMemo(
    () => resolveReferenceLineOverflowMode({ useNormalizedAxis }),
    [useNormalizedAxis],
  );
  const isPrimaryYAxisReversed = isVizAxisReversed(axisDirections, "primary");
  const isSecondaryYAxisReversed = isVizAxisReversed(
    axisDirections,
    "secondary",
  );
  const resolveReferenceYAxisId = useCallback(
    (axis: "primary" | "secondary") => {
      if (!useDualAxis) return undefined;
      return resolveTimeSeriesReferenceLineAxisId({
        useDualAxis,
        axis,
      });
    },
    [useDualAxis],
  );

  const shouldFormatAxisToPercent = useMemo(() => {
    if (useNormalizedAxis) return false;
    if (renderedSeriesList.length === 0) return false;
    if (percentKeys.length === 0) return false;
    return percentKeys.length === renderedSeriesList.length;
  }, [percentKeys.length, renderedSeriesList.length, useNormalizedAxis]);

  const dualAxisIdByMetricKey = useMemo(() => {
    const firstMetricKey = metricGroupKeys[0];
    const secondMetricKey = metricGroupKeys[1];
    const next = new Map<string, "left" | "right">();
    if (firstMetricKey) next.set(firstMetricKey, "left");
    if (secondMetricKey) next.set(secondMetricKey, "right");
    return next;
  }, [metricGroupKeys]);

  const dualAxisTickFormatter = useMemo(() => {
    const resolveFormatter = (metricKey: string | undefined) => {
      if (!metricKey) return formatValueTick;
      const hasPercentSeries = renderedSeriesList
        .filter((entry) => entry.metricKey === metricKey)
        .some((entry) => isPercentFieldKey(entry.key, metaByName));
      return hasPercentSeries ? formatPercentTick : formatValueTick;
    };
    return {
      left: resolveFormatter(metricGroupKeys[0]),
      right: resolveFormatter(metricGroupKeys[1]),
    };
  }, [metaByName, metricGroupKeys, renderedSeriesList]);
  const leftAxisSeriesKeys = useMemo(
    () =>
      renderedSeriesList
        .filter(
          (entry) => dualAxisIdByMetricKey.get(entry.metricKey) !== "right",
        )
        .map((entry) => entry.key),
    [dualAxisIdByMetricKey, renderedSeriesList],
  );
  const rightAxisSeriesKeys = useMemo(
    () =>
      renderedSeriesList
        .filter(
          (entry) => dualAxisIdByMetricKey.get(entry.metricKey) === "right",
        )
        .map((entry) => entry.key),
    [dualAxisIdByMetricKey, renderedSeriesList],
  );

  const chartConfig = useMemo(() => {
    return Object.fromEntries(
      allRenderedSeriesList.map((entry) => {
        return [
          entry.key,
          {
            label: entry.label,
            color: entry.color,
          },
        ];
      }),
    );
  }, [allRenderedSeriesList]);

  const chartData = useMemo(() => {
    const axisSeriesKeys = renderedSeriesList.map((entry) => entry.key);
    const normalizationGroupByKey = new Map(
      renderedSeriesList.map((entry) => [entry.key, entry.metricKey] as const),
    );
    // current/comparison を同じ metric 単位で正規化して、系列間の形状比較を維持する。
    return normalizeRowsForAxis({
      rows: sortedData,
      axisSeriesKeys,
      useNormalizedAxis,
      normalizationGroupByKey,
    });
  }, [renderedSeriesList, sortedData, useNormalizedAxis]);
  const resolvedReferenceLines = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );
  const resolvedReferenceAreas = useMemo(
    () => resolveVizReferenceAreas(referenceAreas),
    [referenceAreas],
  );

  const formatXAxisTick = useCallback(
    (value: unknown) => {
      if (value === null || value === undefined) return "";
      const label = String(value);
      return formatVizAxisDateLabel(label, i18n);
    },
    [i18n],
  );

  const formatTooltipLabel = useCallback(
    (value: unknown) => formatVizTooltipDomainLabel(value, i18n),
    [i18n],
  );

  const rotateXAxis = useMemo(() => {
    return sortedData.slice(0, 12).some((row) => {
      const raw = String(row[resolvedXKey] ?? "");
      const formatted = formatXAxisTick(row[resolvedXKey]);
      return isIsoDateLike(raw) || formatted.length > 10;
    });
  }, [formatXAxisTick, resolvedXKey, sortedData]);
  const xAxisTickLayout = useMemo(() => {
    const labels = sortedData
      .slice(0, 12)
      .map((row) => formatXAxisTick(row[resolvedXKey]));
    return resolveXAxisTickLayout({
      rotate: rotateXAxis,
      labels,
      extraLines: hasComparingDate ? 2 : 1,
    });
  }, [
    formatXAxisTick,
    hasComparingDate,
    resolvedXKey,
    rotateXAxis,
    sortedData,
  ]);
  const xAxisTickDensityConfig = useMemo(() => {
    return resolveXAxisTickDensityConfig({
      density: resolveEffectiveXAxisTickDensity({
        density: xAxisTicks?.density,
        isMobile,
      }),
      rowCount: chartData.length,
      baseMinTickGap: xAxisTickLayout.minTickGap,
    });
  }, [
    chartData.length,
    isMobile,
    xAxisTickLayout.minTickGap,
    xAxisTicks?.density,
  ]);
  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );
  const leftYAxisWidth = useMemo(
    () =>
      resolveTimeSeriesNumericAxisWidth({
        rows: chartData,
        seriesKeys: useDualAxis
          ? leftAxisSeriesKeys
          : renderedSeriesList.map((entry) => entry.key),
        tickFormatter: useDualAxis
          ? dualAxisTickFormatter.left
          : shouldFormatAxisToPercent
            ? formatPercentTick
            : formatValueTick,
        tickFontSize: axisStyle.tickFontSize,
        isMobile,
      }),
    [
      axisStyle.tickFontSize,
      chartData,
      dualAxisTickFormatter.left,
      isMobile,
      leftAxisSeriesKeys,
      renderedSeriesList,
      shouldFormatAxisToPercent,
      useDualAxis,
    ],
  );
  const rightYAxisWidth = useMemo(
    () =>
      useDualAxis
        ? resolveTimeSeriesNumericAxisWidth({
            rows: chartData,
            seriesKeys: rightAxisSeriesKeys,
            tickFormatter: dualAxisTickFormatter.right,
            tickFontSize: axisStyle.tickFontSize,
            isMobile,
          })
        : 0,
    [
      axisStyle.tickFontSize,
      chartData,
      dualAxisTickFormatter.right,
      isMobile,
      rightAxisSeriesKeys,
      useDualAxis,
    ],
  );
  const chartRightMargin = useMemo(() => {
    if (!useDualAxis) return 16;
    return resolveTimeSeriesAxisOuterMargin({
      axisWidth: rightYAxisWidth,
      isMobile,
    });
  }, [isMobile, rightYAxisWidth, useDualAxis]);
  const xAxisHeight = hasComparingDate
    ? Math.max(xAxisTickLayout.height, rotateXAxis ? 92 : 56)
    : xAxisTickLayout.height;
  const chartBottomMargin = hasComparingDate
    ? Math.max(xAxisTickLayout.bottomMargin, rotateXAxis ? 18 : 22)
    : xAxisTickLayout.bottomMargin;
  const resolvedChartHeight = resolveTimeSeriesChartHeight({
    chartHeight,
    hasDenseXAxis: rotateXAxis || hasComparingDate,
    isMobile,
  });

  const renderComparingDateTick = useCallback(
    (props: CompareXAxisTickProps) => {
      const x = typeof props.x === "number" ? props.x : Number(props.x) || 0;
      const y = typeof props.y === "number" ? props.y : Number(props.y) || 0;
      const xOffset = rotateXAxis ? xAxisTickLayout.tickDx : 0;
      const yOffset = rotateXAxis ? xAxisTickLayout.tickDy : 0;
      const tickX = x + xOffset;
      const tickY = y + yOffset;
      const index = props.payload?.index;
      const row = typeof index === "number" ? sortedData[index] : null;
      const currentLabel = formatXAxisTick(props.payload?.value);
      const comparingDate = row
        ? toComparingDateLabel(row.comparingDate)
        : null;
      const comparingLabel = comparingDate
        ? formatXAxisTick(comparingDate)
        : "";
      const firstLineDy = rotateXAxis ? 14 : 12;
      const secondLineDy = rotateXAxis ? 13 : 14;
      const baseTickFontSize = axisStyle.tickFontSize;
      const comparisonFontSize = Math.max(
        8,
        baseTickFontSize + comparisonFontSizeDelta,
      );
      const comparisonFill = comparisonDateMuted
        ? "hsl(var(--muted-foreground))"
        : "hsl(var(--foreground))";
      const comparisonOpacity = comparisonDateMuted ? 0.65 : 1;

      return (
        <text
          x={tickX}
          y={tickY}
          fill="hsl(var(--muted-foreground))"
          fontSize={baseTickFontSize}
          textAnchor={rotateXAxis ? "end" : "middle"}
          transform={
            rotateXAxis
              ? `rotate(-45 ${tickX} ${tickY + firstLineDy})`
              : undefined
          }
        >
          <tspan x={tickX} dy={firstLineDy} fontSize={baseTickFontSize}>
            {currentLabel}
          </tspan>
          <tspan
            x={tickX}
            dy={secondLineDy}
            fill={comparisonFill}
            fontSize={comparisonFontSize}
            opacity={comparisonOpacity}
          >
            {comparingLabel}
          </tspan>
        </text>
      );
    },
    [
      formatXAxisTick,
      rotateXAxis,
      sortedData,
      axisStyle.tickFontSize,
      comparisonDateMuted,
      comparisonFontSizeDelta,
      xAxisTickLayout.tickDx,
      xAxisTickLayout.tickDy,
    ],
  );
  const xAxisTick = useMemo(() => {
    if (hasComparingDate) return renderComparingDateTick;
    if (!rotateXAxis) {
      return {
        fontSize: axisStyle.tickFontSize,
      };
    }
    return {
      dx: xAxisTickLayout.tickDx,
      dy: xAxisTickLayout.tickDy,
      fontSize: axisStyle.tickFontSize,
    };
  }, [
    axisStyle.tickFontSize,
    hasComparingDate,
    renderComparingDateTick,
    rotateXAxis,
    xAxisTickLayout.tickDx,
    xAxisTickLayout.tickDy,
  ]);

  const resolvedExportFormats = ensurePngExportFormats(
    normalizeVizExportFormats(exportFormats, DEFAULT_EXPORT_FORMATS),
  );
  const handleExport = useCallback(
    async (format: VizExportFormat) => {
      if (format === "csv") {
        downloadRowsAsCsv({
          rows: sortedData,
          columns: [
            resolvedXKey,
            ...renderedSeriesList.map((entry) => entry.key),
          ],
          baseName: "viz-time-series",
        });
        return;
      }

      if (format === "json") {
        downloadRowsAsJson({
          rows: sortedData,
          columns: [
            resolvedXKey,
            ...renderedSeriesList.map((entry) => entry.key),
          ],
          baseName: "viz-time-series",
        });
        return;
      }

      await downloadVizAsPng({
        element: containerRef.current,
        baseName: "viz-time-series",
      });
    },
    [renderedSeriesList, resolvedXKey, sortedData],
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  const seriesMenuItems = useMemo(() => {
    const seen = new Set<string>();
    return seriesList.flatMap((entry) => {
      const toggleKey = resolveSeriesToggleKey(entry);
      if (seen.has(toggleKey) || !renderableToggleKeySet.has(toggleKey)) {
        return [];
      }
      seen.add(toggleKey);
      return [
        {
          key: toggleKey,
          label: resolveSeriesToggleLabel(entry),
        },
      ];
    });
  }, [renderableToggleKeySet, seriesList]);

  const legendPayload = useMemo(() => {
    return renderableSeriesList.map((entry) => {
      return {
        dataKey: entry.key,
        value: entry.label,
        color: chartConfig[entry.key]?.color ?? "var(--chart-1)",
        payload: {
          toggleKey: entry.toggleKey,
        },
      };
    });
  }, [chartConfig, renderableSeriesList]);
  const legendLayout = useMemo(
    () =>
      resolveAiLegendLayout({
        itemCount: legendPayload.length,
        isMobile,
        isExpanded: isLegendExpanded,
        maxVisibleRows: 2,
      }),
    [isLegendExpanded, isMobile, legendPayload.length],
  );

  const comparisonTooltipBaseKeys = useMemo(
    () => [...currentToComparisonSeriesMap.keys()],
    [currentToComparisonSeriesMap],
  );
  const comparisonTooltipPrefix = useMemo(() => {
    const keys = [...currentToComparisonSeriesMap.values()];
    if (keys.length === 0) return "comparing_";
    if (keys.every((key) => key.startsWith("previous"))) return "previous";
    if (keys.every((key) => key.startsWith("comparing_"))) return "comparing_";
    return "comparing_";
  }, [currentToComparisonSeriesMap]);

  const hasComparisonTooltip = useMemo(
    () => hasComparingDate && currentToComparisonSeriesMap.size > 0,
    [currentToComparisonSeriesMap.size, hasComparingDate],
  );
  const comparisonFormatToPercentFields = useMemo(() => {
    return percentBaseKeys;
  }, [percentBaseKeys]);

  const standardTooltipPercentFields = useMemo(() => {
    return percentKeys;
  }, [percentKeys]);
  const formatStandardTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: standardTooltipPercentFields.includes(key),
      }),
    [standardTooltipPercentFields],
  );
  const formatComparisonTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: comparisonFormatToPercentFields.includes(key),
      }),
    [comparisonFormatToPercentFields],
  );
  const formatComparisonTooltipDiff = useCallback(
    (diff: number, key: string) =>
      formatVizTooltipDiffValue(diff, {
        diffType: "absolute",
        isPercent: comparisonFormatToPercentFields.includes(key),
      }),
    [comparisonFormatToPercentFields],
  );

  return (
    <div ref={containerRef} className="w-full">
      <TimeSeriesActionBar
        allowSeriesToggle={allowSeriesToggle}
        allowExport={allowExport}
        seriesMenuItems={seriesMenuItems}
        visibleKeySet={visibleToggleKeySet}
        onToggleSeries={toggleSeriesVisibility}
        exportFormats={resolvedExportFormats}
        onExport={handleExport}
        onCopyImage={handleCopyImage}
        hasChartData={chartData.length > 0 && renderedSeriesList.length > 0}
        alwaysRender
      />

      <ChartContainer
        config={chartConfig}
        className="w-full aspect-auto rounded-[18px]"
        style={{ height: resolvedChartHeight }}
      >
        <LineChart
          data={chartData}
          margin={{
            top: 18,
            right: chartRightMargin + 4,
            bottom: chartBottomMargin + 2,
            left: 2,
          }}
        >
          <CartesianGrid
            {...resolveVizCartesianGridProps({
              axisStyle,
              yAxisId: useDualAxis ? "left" : undefined,
            })}
          />
          <XAxis
            type="category"
            dataKey={resolvedXKey}
            tickLine={resolveVizAxisTickLineProps()}
            axisLine={resolveVizAxisLineProps()}
            tickFormatter={formatXAxisTick}
            tickMargin={Math.max(
              xAxisTickLayout.tickMargin,
              axisStyle.tickMargin,
            )}
            minTickGap={xAxisTickDensityConfig.minTickGap}
            interval={xAxisTickDensityConfig.interval}
            angle={xAxisTickLayout.angle}
            textAnchor={xAxisTickLayout.textAnchor}
            height={xAxisHeight}
            tick={xAxisTick}
          />
          {useDualAxis ? (
            <>
              <YAxis
                yAxisId="left"
                type="number"
                tickFormatter={dualAxisTickFormatter.left}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tickMargin={axisStyle.tickMargin}
                width={leftYAxisWidth}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isPrimaryYAxisReversed}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                type="number"
                tickFormatter={dualAxisTickFormatter.right}
                tickLine={resolveVizAxisTickLineProps()}
                axisLine={resolveVizAxisLineProps()}
                tickMargin={axisStyle.tickMargin}
                width={rightYAxisWidth}
                tick={{ fontSize: axisStyle.tickFontSize }}
                reversed={isSecondaryYAxisReversed}
              />
            </>
          ) : (
            <YAxis
              type="number"
              tickFormatter={
                shouldFormatAxisToPercent
                  ? (value: unknown) => formatPercentTick(value)
                  : (value: unknown) => formatValueTick(value)
              }
              tickLine={resolveVizAxisTickLineProps()}
              axisLine={resolveVizAxisLineProps()}
              tickMargin={axisStyle.tickMargin}
              width={leftYAxisWidth}
              tick={{ fontSize: axisStyle.tickFontSize }}
              domain={useNormalizedAxis ? [0, 100] : undefined}
              reversed={isPrimaryYAxisReversed}
            />
          )}
          {resolvedReferenceAreas.map((area) => (
            <ReferenceArea
              key={area.key}
              y1={area.from}
              y2={area.to}
              yAxisId={resolveReferenceYAxisId(area.axis)}
              stroke={area.color}
              fill={area.color}
              fillOpacity={area.opacity}
              ifOverflow={referenceLineOverflowMode}
              label={resolveVizReferenceLabelProps({
                label: area.label,
                color: area.color,
                position: "insideTopLeft",
                dx: 8,
                dy: 6,
              })}
            />
          ))}
          {resolvedReferenceLines.map((line) => (
            <ReferenceLine
              key={line.key}
              y={line.value}
              yAxisId={resolveReferenceYAxisId(line.axis)}
              {...resolveVizSemanticReferenceLineProps({ color: line.color })}
              ifOverflow={referenceLineOverflowMode}
              label={resolveVizReferenceLabelProps({
                label: line.label,
                color: line.color,
                dy: -6,
              })}
            />
          ))}
          <ChartTooltip
            content={
              hasComparisonTooltip ? (
                <ComparisonChartTooltipContent
                  comparisonKeyPrefix={comparisonTooltipPrefix}
                  comparisonDateKey="comparingDate"
                  allowedKeys={comparisonTooltipBaseKeys}
                  formatToPercentFields={comparisonFormatToPercentFields}
                  formatBaseDate={(value) =>
                    formatVizTooltipDateLabel(String(value ?? ""), i18n)
                  }
                  formatComparisonDate={(value) =>
                    formatVizTooltipDateLabel(String(value ?? ""), i18n)
                  }
                  formatValue={formatComparisonTooltipValue}
                  formatDiff={formatComparisonTooltipDiff}
                  isGrouped={false}
                  resolveScaledValue={useNormalizedAxis}
                />
              ) : (
                <ChartTooltipContent
                  labelKey={resolvedXKey}
                  labelClassName={
                    resolvedTooltipMode === "compact"
                      ? "text-[11px]"
                      : undefined
                  }
                  nameKey="dataKey"
                  labelFormatter={formatTooltipLabel}
                  allowedKeys={renderedSeriesList.map((entry) => entry.key)}
                  formatToPercentFields={standardTooltipPercentFields}
                  valueFormatter={formatStandardTooltipValue}
                  isGrouped={false}
                  resolveScaledValue={useNormalizedAxis}
                />
              )
            }
          />
          {renderedSeriesList.map((entry) => {
            const resolvedColor =
              chartConfig[entry.key]?.color ?? "var(--chart-1)";
            const displayLabel = chartConfig[entry.key]?.label;
            const yAxisId = useDualAxis
              ? (dualAxisIdByMetricKey.get(entry.metricKey) ?? "left")
              : undefined;
            const isComparisonEntry = isComparisonSeriesKey(entry.key);
            const lineInterpolation = resolveTimeSeriesLineInterpolation({
              dataKey: entry.key,
              metricKey: entry.metricKey,
              fieldMetaMap: metaByName,
            });
            const staticDotProps = resolveTimeSeriesStaticDotProps({
              finitePointCount: finitePointCountBySeriesKey.get(entry.key),
              color: resolvedColor,
              radius: isComparisonEntry ? 2.5 : 3,
            });

            return (
              <Line
                key={entry.key}
                dataKey={entry.key}
                name={resolveDisplayLabel(displayLabel, entry.label, entry.key)}
                stroke={resolvedColor}
                strokeWidth={isComparisonEntry ? 1.75 : 2.5}
                strokeOpacity={isComparisonEntry ? 0.72 : 1}
                strokeDasharray={
                  entry.strokeDasharray ??
                  (isComparisonEntry ? COMPARISON_SERIES_DASHARRAY : undefined)
                }
                type={lineInterpolation}
                dot={staticDotProps}
                activeDot={resolveVizActiveDotProps({
                  radius: isComparisonEntry ? 3 : 4,
                  color: resolvedColor,
                })}
                yAxisId={yAxisId}
                isAnimationActive={false}
                animationBegin={0}
                animationDuration={0}
              />
            );
          })}
        </LineChart>
      </ChartContainer>
      {/* Expanded legend rows must live outside Recharts so the card grows instead of covering the plot area. */}
      {legendPayload.length > 0 ? (
        <AiChartLegendContent
          className={legendLayout.className}
          payload={legendPayload}
          visibleKeySet={visibleToggleKeySet}
          onToggleItem={
            allowSeriesToggle !== false ? toggleSeriesVisibility : undefined
          }
          maxVisibleItems={legendLayout.maxVisibleItems}
          isExpanded={isLegendExpanded}
          onExpandedChange={setIsLegendExpanded}
        />
      ) : null}
    </div>
  );
}
